/**
 * The colours the app is currently wearing.
 *
 * Every component in the app already reads `theme.bg`, `theme.orange` and so
 * on, in about 900 places. Making the palette switchable could have meant
 * threading a React context through all of them, or rewriting every colour as
 * a CSS variable.
 *
 * It does neither. `theme` is a **live view** of whichever palette is
 * currently chosen: reading `theme.orange` asks the active palette, so not one
 * of those 900 call sites changes. `applyTheme` swaps what it points at.
 *
 * **The catch, stated plainly.** React does not know a plain object changed,
 * so nothing re-renders by itself. `applyTheme` is therefore called alongside
 * a state change in `App`, which re-renders the tree — and the tree then reads
 * the new colours on its way past. That coupling is the price of not touching
 * 900 lines, and it is written down here so the next person does not spend an
 * afternoon wondering why a colour changed but the screen did not.
 *
 * The palettes themselves are in `themes.ts`, along with why there are five of
 * them and why none of them is ever unlocked as a reward.
 */
import { DEFAULT_THEME, SPROUT, paletteFor } from './themes'
import type { Palette, ThemeId } from './themes'

let active: Palette = { ...SPROUT }
let activeId: ThemeId = DEFAULT_THEME

/** Read-only view of the live palette. Never reassign a property on this. */
export const theme = new Proxy({} as Palette, {
  get: (_t, key: string) => active[key as keyof Palette],
  // So that `{...theme}` and Object.keys(theme) behave, which a few spread
  // sites rely on.
  ownKeys: () => Reflect.ownKeys(active),
  getOwnPropertyDescriptor: (_t, key) =>
    ({ ...Object.getOwnPropertyDescriptor(active, key), configurable: true }),
  has: (_t, key) => key in active,
  set: () => {
    throw new Error('theme is read-only; call applyTheme(id) instead')
  },
}) as Palette

export function applyTheme(id: string | null | undefined): void {
  active = paletteFor(id)
  activeId = (id as ThemeId) ?? DEFAULT_THEME
  try {
    // The page background sits outside React, so it has to be told separately
    // or the app floats on the old colour.
    document.body.style.background = active.bg
    document.body.style.color = active.text
    document.documentElement.style.colorScheme =
      activeId === 'midnight' ? 'dark' : 'light'
  } catch {
    /* no document in tests */
  }
}

export function currentThemeId(): ThemeId {
  return activeId
}

/**
 * Two rounded, friendly typefaces, bundled with the app (public/fonts).
 *
 * Until 4 Oct 2026 this named 'Source Sans 3', which was never loaded, so
 * every screen was in the system font. Nunito for anything a child reads —
 * rounded, open letters, a single-storey "a" at heavier weights — and
 * Fredoka for headings, which is where the cartoon feel comes from.
 *
 * Self-hosted rather than fetched from Google Fonts, so a child's browser
 * does not contact Google just to draw text. Both are under the SIL Open
 * Font License 1.1, whose text travels with them in public/fonts.
 */
export const font = {
  body: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  display: "'Fredoka', 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
} as const

export const radius = { sm: 8, md: 10, lg: 14, xl: 20, pill: 999 } as const

/** Never below 44px for anything a child taps. Small fingers, fast hands. */
export const TAP_MIN = 44

export const space = (n: number) => `${n * 4}px`

export type { Palette, ThemeId } from './themes'
